import { useCallback, useEffect, useRef, useState } from 'react';
import './layer-collapse.less';
import classnames from 'classnames';
import { cloneDeep, maxBy, minBy } from 'lodash';
import { ReactSortable } from 'react-sortablejs';
import { v4 as uuid } from 'uuid';
import { Scrollbars } from '@meitu/saas-ui';
import { copyOriginIndex, HOTKEY_TYPE } from '@meitu/saas-editor-core/es/framework';
import { getRotatePosition } from '@meitu/saas-editor-core/es/utils/collision-kit';
import { isHotKeyPress } from '@meitu/saas-editor-core/es/utils/event-kit';
import { getGroupLayerProps } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { IBackgroundLayerProps, ILayerProps, IPoint, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { editor } from '@/instance';
import LayerItem from './layer-item';
import { getAllParentLayerByLayerId, reverseLayers } from './utils';

// Sortable.mount(new MultiDrag());
const renderLayer = (
    layer: ILayerProps,
    onAddLayer: (layers: ILayerProps[], newIndex: number) => void,
    onRemoveLayer: (layers: ILayerProps[], oldIndex: number) => void,
    onEndHandler: (layers: ILayerProps[], oldIndex: number, newIndex: number) => void,
    isChild: boolean = false,
    setDisableOperate: (disable: boolean) => void,
    disableOperate: boolean,
    parentIsVisible: boolean,
    activeLayerId: string | null,
    selectIds: string[],
    selectLayer: Function,
    handleMakeGroup: Function,
) => {
    return (
        <div key={layer.id} style={{ paddingLeft: isChild ? 18 : 0 }}>
            <LayerItem
                layer={layer}
                setDisableOperate={setDisableOperate}
                parentIsVisible={parentIsVisible}
                selectIds={selectIds}
                selectLayer={selectLayer}
                handleMakeGroup={handleMakeGroup}
                activeLayerId={activeLayerId}
            />
            {layer.children.length > 0 && layer.extraInfo?.isExpanded && (
                <ReactSortable
                    multiDrag={true}
                    selectedClass="selected"
                    disabled={disableOperate}
                    animation={150}
                    list={layer.children}
                    setList={() => {}}
                    group="group"
                    onEnd={(evt) => {
                        if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
                            onEndHandler(layer.children, evt.oldIndex, evt.newIndex);
                        }
                    }}
                    onAdd={(evt) => {
                        if (evt.newIndex !== undefined) {
                            onAddLayer(layer.children, evt.newIndex);
                        }
                    }}
                    onRemove={(evt) => {
                        if (evt.oldIndex !== undefined) {
                            onRemoveLayer(layer.children, evt.oldIndex);
                        }
                    }}
                    onStart={(evt) => {
                        if (evt.oldIndex !== undefined) {
                            if (layer.children[evt.oldIndex] && !selectIds.includes(layer.children[evt.oldIndex].id)) {
                                selectLayer(layer.children[evt.oldIndex]);
                            }
                        }
                    }}
                >
                    {layer.children.map((child: ILayerProps) =>
                        renderLayer(
                            child,
                            onAddLayer,
                            onRemoveLayer,
                            onEndHandler,
                            true,
                            setDisableOperate,
                            disableOperate,
                            !layer.hidden && parentIsVisible,
                            activeLayerId,
                            selectIds,
                            selectLayer,
                            handleMakeGroup,
                        ),
                    )}
                </ReactSortable>
            )}
        </div>
    );
};

const LayerCollapse = () => {
    const [currentLayers, setCurrentLayers] = useState<ILayerProps[]>(reverseLayers(editor.active.getLayers()));
    const [bgLayer, setBgLayer] = useState<ILayerProps | null>(editor.backgroundLayer.getBackgroundLayer());

    const [activeLayerId, setActiveLayerId] = useState<string | null>(editor.active.getPick()?.id || null);
    const activeLayerIdRef = useRef<string | null>(null);
    const isBaseOperateRef = useRef<boolean>(false);
    const [disableOperate, setDisableOperate] = useState<boolean>(false);
    const isDraggingRef = useRef<boolean>(false);
    const [selectIds, setSelectIds] = useState<string[]>([]);
    const defaultSelectIndex = useRef(-1);

    useEffect(() => {
        // 禁用操作
        return editor.onState(({ isBaseOperate }) => {
            if (isBaseOperate) {
                isBaseOperateRef.current = true;
            } else {
                isBaseOperateRef.current = false;
            }
        });
    }, []);

    useEffect(() => {
        const handle = editor.onHotkey(HOTKEY_TYPE.makeGroup, () => {
            handleMakeGroup();
        });
        return () => {
            editor.off(handle);
        };
    }, [currentLayers, selectIds]);

    useEffect(() => {
        // 提示语
    }, [selectIds]);

    useEffect(() => {
        const handle = editor.active.onStageChange(({ stageIndex }) => {
            if (isBaseOperateRef.current) {
                return;
            }
            console.log('onStageChange==');
            const layers = editor.active.getLayers();
            const currentLayers = reverseLayers(layers);
            if (defaultSelectIndex.current !== -1) {
                const layer = currentLayers[defaultSelectIndex.current];
                if (layer) {
                    editor.active.set(layer.id);
                }
                defaultSelectIndex.current = -1;
                console.log(layer);
            }
            setCurrentLayers(currentLayers);
            console.log(currentLayers);
            setBgLayer(editor.backgroundLayer.getBackgroundLayer());
            isDraggingRef.current = false;
        });

        const handle2 = editor.stage.onLayerChange(() => {
            if (isBaseOperateRef.current || isDraggingRef.current) {
                return;
            }
            console.log('onLayerChange==');
            // FIXME 不知道为什么editor.active.getLayers()没法马上拿到最新的数据
            setTimeout(() => {
                const layers = editor.active.getLayers();
                const currentLayers = reverseLayers(layers);
                if (defaultSelectIndex.current !== -1) {
                    const layer = currentLayers[defaultSelectIndex.current];
                    if (layer) {
                        editor.active.set(layer.id);
                    }
                    defaultSelectIndex.current = -1;
                    console.log(layer);
                }
                setCurrentLayers(currentLayers);
                setBgLayer(editor.backgroundLayer.getBackgroundLayer());
            }, 50);
        });

        const handle3 = editor.active.onChange((layer, pickLayer) => {
            if (isBaseOperateRef.current) {
                return;
            }
            console.log('active.onChange==');
            setActiveLayerId(pickLayer?.id || layer?.id || null);
            const id = pickLayer?.id || layer?.id;

            if (!id) {
                activeLayerIdRef.current = '';
                setSelectIds([]);
                return;
            } else if (activeLayerIdRef.current === id) {
                return;
            }
            selectLayer(pickLayer || layer);
            activeLayerIdRef.current = id || '';

            const layers = editor.active.getLayers();
            const parentLayers: ILayerProps[] = [];
            getAllParentLayerByLayerId(layers, id, parentLayers);

            if (!(parentLayers.length > 0)) {
                return;
            }

            const noExpendLayers = parentLayers.filter((item) => !item.extraInfo?.isExpanded);
            if (noExpendLayers.length > 0) {
                noExpendLayers.forEach((item) => {
                    item.extraInfo = { ...item.extraInfo, isExpanded: true };
                });
                editor.stage.updateLayer(parentLayers[parentLayers.length - 1]);

                setTimeout(() => {
                    setCurrentLayers(reverseLayers(editor.active.getLayers()));
                    setBgLayer(editor.backgroundLayer.getBackgroundLayer());
                }, 10);
            }
        });

        return () => {
            editor.off(handle);
            editor.off(handle2);
            editor.off(handle3);
            activeLayerIdRef.current = '';
        };
    }, []);

    const handleMakeGroup = () => {
        makeGroup(currentLayers, selectIds);
    };

    const testParentSelected = (layer: ILayerProps) => {
        let ids: string[] = [];
        const parentId = layer.parentId;
        if (!parentId) return ids;
        if (selectIds.includes(parentId)) return [parentId];
        const parent = editor.stage.getLayer(parentId);
        if (parent) ids = [...ids, ...testParentSelected(parent)];
        return ids;
    };

    const testChildSelected = (layer: ILayerProps) => {
        let ids: string[] = [];
        const children = layer.children;
        if (!children.length) return ids;
        children.forEach((el) => {
            if (selectIds.includes(el.id)) {
                ids.push(el.id);
            }
            ids = [...ids, ...testChildSelected(el)];
        });
        return ids;
    };

    const selectLayer = (layer: ILayerProps | null, isAdd: boolean = false) => {
        if (!layer) return;
        const id = layer.id;
        if (isHotKeyPress() || isAdd) {
            const rmIds = [...testParentSelected(layer), ...testChildSelected(layer)];
            setSelectIds((pre) => {
                pre = pre.filter((el) => !rmIds.includes(el));
                const index = pre.findIndex((el) => el === id);
                if (index !== -1) {
                    pre.splice(index, 1);
                }
                return [...pre, id];
            });
        } else {
            setSelectIds([id]);
        }
    };

    const flattenTree = (layers: ILayerProps[]) => {
        const results: ILayerProps[] = [];
        for (const layer of layers) {
            if (layer.children && layer.children.length > 0) {
                const children = layer.children ? flattenTree(layer.children) : [];
                if (children.length <= 1) {
                    if (children.length === 1) {
                        children[0].parentId = layer.parentId;
                        results.push(children[0]);
                    }
                } else {
                    children.forEach((el) => {
                        el.parentId = layer.id;
                    });
                    results.push({ ...layer, children });
                }
            } else if (layer.layerType !== LayerType.GROUP_LAYER) {
                results.push(layer);
            }
        }
        return results;
    };

    const makeGroup = (currentLayers: ILayerProps[], selectIds: string[]) => {
        const res = collectLayers(currentLayers, selectIds);

        const groupLayers = res.layers;

        if (groupLayers.length <= 1) return;

        let { targetIndex, targetLayers } = res;

        const parentId = groupLayers[0].parentId;

        const isTop =
            !groupLayers.every((el) => el.parentId === parentId) ||
            groupLayers.every((el) => !el.parentId && !parentId);

        if (isTop) {
            targetIndex = 0;
            targetLayers = currentLayers;
        }

        const groupLayer = createGroup(groupLayers);

        groupLayer.extraInfo = { ...groupLayer.extraInfo, isExpanded: true };

        if (groupLayer && targetIndex !== -1) {
            targetLayers.splice(targetIndex, 0, groupLayer);
        }
        const newTree = flattenTree(currentLayers);
        setCurrentLayers([...newTree]);
        const resultLayer = [editor.backgroundLayer.getBackgroundLayer()!, ...reverseLayers([...newTree])];
        editor.stage.replaceActive(resultLayer, editor.stage.getExtraInfo(editor.active.getActiveStageIndex()), false);
        if (isTop) {
            defaultSelectIndex.current = 0;
        } else setSelectIds([]);
    };

    const createGroup = (children: ILayerProps[]) => {
        const positions: IPoint[] = [];
        const id = copyOriginIndex(uuid(), children[0].id);
        children.forEach((layer) => {
            const { left, top, width, height, rotate } = layer;
            const position = getRotatePosition({ left, top, width, height, rotate });
            positions.push(...position);
            layer.parentId = id;
        });
        const minX = minBy(positions, (pos) => pos.x) as IPoint;
        const maxX = maxBy(positions, (pos) => pos.x) as IPoint;
        const minY = minBy(positions, (pos) => pos.y) as IPoint;
        const maxY = maxBy(positions, (pos) => pos.y) as IPoint;
        const baseInfo = {
            left: minX.x,
            top: minY.y,
            width: maxX.x - minX.x,
            height: maxY.y - minY.y,
            rotate: 0,
            children,
        };
        return getGroupLayerProps({
            id,
            ...baseInfo,
            data: {
                isExtraGroup: false,
            },
        });
    };

    const collectLayers = (currentLayers: ILayerProps[], selectIds: string[], rm: boolean = true) => {
        const layers: { layer: ILayerProps; source: ILayerProps[] }[] = [];
        let targetIndex = -1;
        let targetLayers: ILayerProps[] = [];
        traverseTrees(currentLayers, (layer) => {
            // 顶层
            if (selectIds.includes(layer.id)) {
                const index = currentLayers.findIndex((el) => el.id === layer.id);
                if (index !== -1) {
                    layers.push({ layer, source: currentLayers });
                    if (targetIndex === -1) {
                        targetIndex = index;
                        targetLayers = currentLayers;
                    }
                }
            }
            if (layer.children && layer.children.length) {
                for (let i = 0; i < layer.children.length; i++) {
                    const child = layer.children[i];
                    if (selectIds.includes(child.id)) {
                        const index = layer.children.findIndex((el) => el.id === child.id);
                        if (index !== -1) {
                            layers.push({ layer: layer.children[index], source: layer.children });
                            if (targetIndex === -1) {
                                targetIndex = index;
                                targetLayers = layer.children;
                            }
                        }
                    }
                }
            }
        });

        if (rm) {
            layers.forEach((el) => {
                const index = el.source.findIndex((ele) => ele.id === el.layer.id);
                if (index !== -1) el.source.splice(index, 1);
            });
        }

        return { layers: layers.map((el) => el.layer), targetIndex, targetLayers };
    };

    const moveInfoRef = useRef<{
        oldItems: {
            oldList: ILayerProps[];
            item: ILayerProps;
            index: number;
        }[];
        newItem: {
            currentList: ILayerProps[];
            index: number;
        } | null;
    } | null>(null);

    const onAddLayer = (layers: ILayerProps[], newIndex: number) => {
        if (!moveInfoRef.current) {
            moveInfoRef.current = {
                oldItems: [],
                newItem: null,
            };
        }
        if (newIndex !== undefined) {
            moveInfoRef.current.newItem = {
                currentList: layers,
                index: newIndex,
            };
        }
    };

    const onRemoveLayer = (layers: ILayerProps[], oldIndex: number) => {
        if (!moveInfoRef.current) {
            moveInfoRef.current = {
                oldItems: [],
                newItem: null,
            };
        }
        if (oldIndex !== undefined) {
            moveInfoRef.current.oldItems.push({
                oldList: layers,
                item: layers[oldIndex],
                index: oldIndex,
            });
        }
    };

    const onEndHandler = (layers: ILayerProps[], oldIndex: number, newIndex: number) => {
        isDraggingRef.current = true;
        if (moveInfoRef.current && moveInfoRef.current.newItem) {
            let count = 0;
            const newItem = moveInfoRef.current.newItem;
            newItem.currentList.forEach((el, index) => {
                if (selectIds.includes(el.id) && index < newItem.index) {
                    count++;
                }
            });
            const moveLayers: ILayerProps[] = collectLayers(currentLayers, selectIds).layers;
            moveLayers.forEach((el) => {
                el.parentId = newItem.currentList[0].parentId;
            });

            newItem.currentList.splice(newItem.index - count, 0, ...moveLayers);

            const newTree = flattenTree(currentLayers);
            setCurrentLayers([...newTree]);
            const resultLayer = [editor.backgroundLayer.getBackgroundLayer()!, ...reverseLayers([...newTree])];

            editor.stage.replaceActive(
                resultLayer,
                editor.stage.getExtraInfo(editor.active.getActiveStageIndex()),
                false,
            );
        }
        // 平级移动
        else if (oldIndex !== undefined && newIndex !== undefined) {
            let count = 0;
            const oldItems: ILayerProps[] = [];
            selectIds.forEach((el) => {
                const index = layers.findIndex((ele) => ele.id === el);
                if (index > -1) {
                    if (index < newIndex && layers[index] !== layers[oldIndex]) {
                        count++;
                    }
                    const oldItem = layers[index];
                    layers.splice(index, 1);
                    oldItems.push(oldItem);
                }
            });

            layers.splice(newIndex - count, 0, ...oldItems);
            setCurrentLayers([...currentLayers]);
            const resultLayer = [editor.backgroundLayer.getBackgroundLayer()!, ...reverseLayers([...currentLayers])];

            editor.stage.replaceActive(
                resultLayer,
                editor.stage.getExtraInfo(editor.active.getActiveStageIndex()),
                false,
            );
        }
        setSelectIds([]);
        moveInfoRef.current = null;
    };

    return (
        <div className="c-layer-collapse">
            <div className="c-layer-collapse-title">图层列表</div>
            <div style={{ width: '100%', height: 'calc(100% - 50px)' }}>
                <Scrollbars>
                    {bgLayer ? (
                        <div
                            className={classnames('c-layer-list-item', {
                                active: !selectIds.length,
                            })}
                            onClick={() => {
                                editor.active.delete();
                            }}
                        >
                            <div className="c-layer-list-item__left">
                                <div
                                    className="icon"
                                    style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: 2,
                                        marginRight: 6,
                                        backgroundColor: (bgLayer.data as IBackgroundLayerProps).url
                                            ? ''
                                            : (bgLayer.data as IBackgroundLayerProps).backgroundColor,
                                    }}
                                >
                                    {(bgLayer.data as IBackgroundLayerProps).url ? (
                                        <img
                                            src={(bgLayer.data as IBackgroundLayerProps).url}
                                            style={{ width: 16, height: 16, marginRight: 6, borderRadius: 2 }}
                                            alt=""
                                        />
                                    ) : null}
                                </div>
                                <span>背景</span>
                            </div>
                        </div>
                    ) : null}
                    <ReactSortable
                        multiDrag={true}
                        selectedClass="selected"
                        disabled={disableOperate}
                        animation={150}
                        list={currentLayers}
                        setList={() => {}}
                        group="group"
                        onEnd={(evt) => {
                            if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
                                onEndHandler(currentLayers, evt.oldIndex, evt.newIndex);
                            }
                        }}
                        onAdd={(evt) => {
                            if (evt.newIndex !== undefined) {
                                onAddLayer(currentLayers, evt.newIndex);
                            }
                            console.log('onAdd==');
                        }}
                        onRemove={(evt) => {
                            if (evt.oldIndex !== undefined) {
                                onRemoveLayer(currentLayers, evt.oldIndex);
                            }
                            console.log('onRemove==');
                        }}
                        onStart={(evt) => {
                            if (evt.oldIndex !== undefined) {
                                if (
                                    currentLayers[evt.oldIndex] &&
                                    !selectIds.includes(currentLayers[evt.oldIndex].id)
                                ) {
                                    selectLayer(currentLayers[evt.oldIndex]);
                                }
                            }
                        }}
                    >
                        {currentLayers.map((layer) => {
                            return renderLayer(
                                layer,
                                onAddLayer,
                                onRemoveLayer,
                                onEndHandler,
                                false,
                                setDisableOperate,
                                disableOperate,
                                true,
                                activeLayerIdRef.current,
                                selectIds,
                                selectLayer,
                                handleMakeGroup,
                            );
                        })}
                    </ReactSortable>
                </Scrollbars>
            </div>
        </div>
    );
};

export default LayerCollapse;
